import {
  Component,
  Element,
  h,
  Host,
  Listen,
  Prop,
  State,
} from "@stencil/core";

import state, {
  actAsMyself,
  getCustomers,
  selectCustomer,
} from "@stores/impersonate.store";
import { ImpersonateFlyoutProps, ImpersonateUser } from "./f-impersonate-model";

@Component({
  tag: "f-impersonate-flyout",
  styleUrl: "f-impersonate-flyout.scss",
  shadow: true,
})
export class FImpersonateFlyout {
  @Element() el: HTMLFImpersonateFlyoutElement;

  @Prop() buttonLabel: ImpersonateFlyoutProps["buttonHiddenLabel"] =
    "Impersonate";
  @Prop() buttonHiddenLabel: ImpersonateFlyoutProps["buttonHiddenLabel"] =
    "Personification";
  @Prop() buttonTitle: ImpersonateFlyoutProps["buttonTitle"] =
    this.buttonHiddenLabel;
  @Prop() noCustomerLabel: ImpersonateFlyoutProps["noCustomerLabel"] =
    "Choose customer";
  @Prop()
  closeButtonHiddenLabel: ImpersonateFlyoutProps["closeButtonHiddenLabel"] = (
    window as any
  ).translations["bcp-acc"].close;
  @Prop()
  expanderButtonHiddenLabel: ImpersonateFlyoutProps["expanderButtonHiddenLabel"] =
    (window as any).translations["bcp-acc"].expand;
  @Prop()
  actAsMyselfButtonLabel: ImpersonateFlyoutProps["actAsMyselfButtonLabel"] =
    "Act as myself";
  @Prop() searchPlaceholder: ImpersonateFlyoutProps["errorMessageLabel"] =
    "Customer / City / Tax-nr.";
  @Prop() titleLabel: ImpersonateFlyoutProps["titleLabel"] = "Customers";
  @Prop() emptyListLabel: ImpersonateFlyoutProps["emptyListLabel"] =
    "The customers list is empty";
  @Prop() errorMessageLabel: ImpersonateFlyoutProps["errorMessageLabel"] =
    "An error occurred";
  @Prop() currentCustomerId: ImpersonateFlyoutProps["currentCustomerId"];

  @Prop()
  showActAsMyselfButton: ImpersonateFlyoutProps["showActAsMyselfButton"] = false;
  @Prop() iconOnly: ImpersonateFlyoutProps["iconOnly"] = false;

  @State() shownCustomerList: ImpersonateUser[] = [];

  @Listen("customerSelected", { target: "window" })
  onCustomerSelect(
    e: CustomEvent<{
      customerId: string;
    }>
  ): void {
    selectCustomer(e.detail.customerId);
  }

  async componentWillLoad(): Promise<void> {
    this.shownCustomerList = await getCustomers();
  }

  async componentDidLoad(): Promise<void> {
    if (!this.currentCustomerId) {
      this.flyoutEl.toggleFlyout();
    }
  }

  private getCurrentCustomer(): string {
    return (
      state.customers.find(
        (customer) => customer.customerId === this.currentCustomerId
      )?.customerName ?? this.noCustomerLabel
    );
  }

  private setCustomerList = (customers: ImpersonateUser[]) => {
    this.shownCustomerList = customers;
  };

  private handleActAsMyselfButtonPress = () => {
    actAsMyself();
    this.flyoutEl.toggleFlyout();
  };

  private flyoutEl: HTMLFFlyoutWithButtonElement;

  render() {
    return (
      <Host data-test="impersonate">
        <f-flyout-with-button
          flyoutTitle={`${this.titleLabel} (${state.customers.length})`}
          flyoutId="f-mini-impersonate-flyout"
          ref={(el) => (this.flyoutEl = el)}
          buttonIconName="down-small"
          iconPosition="right"
          buttonTitle={this.buttonTitle}
          closeButtonHiddenLabel={this.closeButtonHiddenLabel}
          flyoutDataTest="flyout-inquiry"
          iconOnly={this.iconOnly}
        >
          {this.buttonLabel}: {this.getCurrentCustomer()}
          <f-visually-hidden>{this.buttonHiddenLabel}</f-visually-hidden>
          <div slot="flyout-feature-bar">
            <f-impersonate-feature-bar
              actAsMyselfButtonLabel={this.actAsMyselfButtonLabel}
              searchPlaceholder={this.searchPlaceholder}
              handleSearchAction={this.setCustomerList}
              handleActAsMyselfButtonPress={this.handleActAsMyselfButtonPress}
              showActAsMyselfButton={this.showActAsMyselfButton}
            />
          </div>
          <div slot="flyout-content">
            {state.flyoutState === "LOADED" && (
              <f-impersonate-customer-list
                emptyListLabel={this.emptyListLabel}
                customers={this.shownCustomerList}
              />
            )}
            {state.flyoutState === "LOADING" && <f-flyout-loader />}
            {state.flyoutState === "ERROR" && (
              <f-flyout-message messageLabel={this.errorMessageLabel} />
            )}
          </div>
        </f-flyout-with-button>
      </Host>
    );
  }
}
